/**
 * Native-platform detection for the Capacitor (iOS/Android) wrapper.
 *
 * Uses the canonical `@capacitor/core` API (scheme-independent, set by the
 * native bridge before app JS runs) with window/protocol fallbacks. Used to
 * branch behaviour that differs between web and the wrapped native app — e.g.
 * suppress the web cookie-consent banner (native uses OS privacy controls) and
 * the faux patriotic scrollbar (native apps don't show a custom scrollbar).
 */
import { Capacitor } from '@capacitor/core';

export function isNativeApp(): boolean {
  try {
    if (typeof Capacitor?.isNativePlatform === 'function' && Capacitor.isNativePlatform()) {
      return true;
    }
  } catch { /* fall through to heuristics */ }
  if (typeof window === 'undefined') return false;
  // Dev-only override so the native chrome can be previewed in a desktop/mobile
  // browser (?native=1 once, persisted). Gated to local/dev hosts so production
  // is never affected.
  try {
    const host = window.location.hostname;
    const isDevHost = host === 'localhost' || host === '127.0.0.1' || host.startsWith('192.168.') || host.startsWith('dev.');
    if (isDevHost) {
      if (new URLSearchParams(window.location.search).get('native') === '1') {
        window.localStorage.setItem('force-native', '1');
      }
      if (window.localStorage.getItem('force-native') === '1') return true;
    }
  } catch { /* ignore */ }
  const w = window as unknown as { Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string } };
  try {
    if (w.Capacitor?.isNativePlatform?.()) return true;
    if (w.Capacitor?.getPlatform && w.Capacitor.getPlatform() !== 'web') return true;
  } catch { /* ignore */ }
  const p = window.location.protocol;
  return p === 'capacitor:' || p === 'ionic:';
}
